// Promos de venta en «Elige el teléfono»: filas del banner por categoría, precio con descuento y burbujas propias.
// La de categoría va solo en el banner y en el precio tachado; la tarjeta no la repite.
import { CalendarPlus, Gift, Smartphone, Tag, Ticket, Wallet, type LucideIcon } from 'lucide-react';
import { formatMXN } from '@/shared/lib/format/money';
import type { PromoBannerRow } from '@/domains/sales/wizard/components/PromoBanner';
import type { BenefitBubble, CardSaleBenefits } from '@/domains/sales/wizard/promotionSlots';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import type { DiscountValue, StoreSalePromotionItem } from '../api/wizardPromotionsContract';
import {
    appliesToItem,
    conditionsText,
    isSureKeepOnly,
    paymentOptionsChipLabel,
} from './promotionAppliesTo';

export type ProductCategory = NonNullable<
    StoreSalePromotionItem['scope']['productCategories']
>[number];
type Levers = StoreSalePromotionItem['levers'];

export const CATEGORY_PLURAL: Record<ProductCategory, string> = {
    PHONE: 'teléfonos',
    BUNDLE: 'kits',
    ACCESSORY: 'accesorios',
    SIM: 'chips',
    MODEM: 'módems',
};

/** Categorías que se ven en «Elige el teléfono». */
const SCREEN_CATEGORIES: readonly ProductCategory[] = ['PHONE', 'BUNDLE'];

/** Categorías que el banner aclara cuando la promo no las incluye. */
const NOTED_EXCLUSIONS: readonly ProductCategory[] = ['BUNDLE', 'ACCESSORY'];

const WHOLE_PESOS = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
});

/** `$150` si son pesos cerrados; con centavos, completo. */
export function pesos(cents: number): string {
    return cents % 100 === 0 ? WHOLE_PESOS.format(cents / 100) : formatMXN(cents / 100);
}

export function matchesList(allowed: readonly string[] | null, value: string | undefined): boolean {
    if (allowed === null) return true;
    return value !== undefined && allowed.includes(value);
}

/** Alcanza al equipo y le toca: una solo SureKeep no va a un equipo que SureKeep no financia. */
export function reachesItem(item: CatalogItem, promo: StoreSalePromotionItem): boolean {
    return inScope(item, promo) && appliesToItem(item, promo);
}

/** Si la promo alcanza al equipo por SKU, gama y categoría. */
export function inScope(item: CatalogItem, promo: StoreSalePromotionItem): boolean {
    const { skus, tiers, productCategories } = promo.scope;
    return (
        matchesList(skus, item.sku) &&
        matchesList(tiers, item.tier) &&
        matchesList(productCategories, item.category)
    );
}

export function percentText(bps: number): string {
    return `${String(bps / 100)} %`;
}

function discountCents(priceCents: number, value: DiscountValue): number {
    if (value.type === 'PERCENTAGE') {
        return Math.round((priceCents * (value.percentBps ?? 0)) / 10_000);
    }
    return Math.min(priceCents, value.amountCents ?? 0);
}

export function discountLabel(value: DiscountValue): string {
    if (value.type === 'PERCENTAGE') return `−${percentText(value.percentBps ?? 0)}`;
    return `−${pesos(value.amountCents ?? 0)}`;
}

/** La promo que más baja el precio del equipo; es una por venta, así que no se suman. */
function bestDeviceDiscount(
    item: CatalogItem,
    promos: readonly StoreSalePromotionItem[],
): { promo: StoreSalePromotionItem; value: DiscountValue; cents: number } | null {
    let best: { promo: StoreSalePromotionItem; value: DiscountValue; cents: number } | null = null;
    for (const promo of promos) {
        const value = promo.levers.deviceDiscount;
        if (!value) continue;
        const cents = discountCents(item.cashPriceCents, value);
        if (cents > 0 && (best === null || cents > best.cents)) best = { promo, value, cents };
    }
    return best;
}

/** Va al banner: no nombra SKUs (gama y categoría sí) y alcanza a teléfonos o kits o a toda la tienda. */
export function isCategoryWide(promo: StoreSalePromotionItem): boolean {
    const { skus, productCategories } = promo.scope;
    if (skus !== null) return false;
    return (
        productCategories === null || productCategories.some((c) => SCREEN_CATEGORIES.includes(c))
    );
}

export interface LeverText {
    key: string;
    icon: LucideIcon;
    /** Para la burbuja: «Cupón $150». */
    label: string;
    /** Dentro de una frase: «cupón $150». */
    phrase: string;
}

/** Color de la burbuja de una palanca, el de su grupo en el detalle. */
export function leverTone(key: string): BenefitBubble['tone'] {
    if (key === 'coupon') return 'coupon';
    if (key === 'gift') return 'gift';
    if (key === 'wallet' || key === 'airtime' || key === 'warranty') return 'benefit';
    return undefined;
}

/** Lo que da cada palanca, sin el descuento del equipo; en el orden en que se le lee al cliente. */
export function otherLevers(levers: Levers): LeverText[] {
    const out: LeverText[] = [];
    if (levers.accessoryDiscount) {
        const acc = levers.accessoryDiscount;
        const value = acc.type === 'AMOUNT' ? (acc.amountCents ?? 0) : null;
        const capped =
            acc.maxAmountCents !== null && (value === null || acc.maxAmountCents < value);
        const text = `${value !== null ? pesos(value) : percentText(acc.percentBps ?? 0)} en accesorios${capped ? ` hasta ${pesos(acc.maxAmountCents ?? 0)}` : ''}`;
        out.push({ key: 'accessory', icon: Tag, label: text, phrase: text });
    }
    if (levers.gift) {
        const n = levers.gift.chooseCount;
        const text = n > 1 ? `${String(n)} regalos` : 'Regalo';
        out.push({ key: 'gift', icon: Gift, label: text, phrase: text.toLowerCase() });
    }
    if (levers.coupon) {
        const { type, amountCents, percentBps } = levers.coupon;
        const value =
            type === 'PERCENTAGE' ? percentText(percentBps ?? 0) : pesos(amountCents ?? 0);
        out.push({
            key: 'coupon',
            icon: Ticket,
            label: `Cupón ${value}`,
            phrase: `cupón ${value}`,
        });
    }
    if (levers.airtime) {
        const { rechargeCount, rechargeAmountCents } = levers.airtime;
        const text = `Tiempo aire ${String(rechargeCount)}×${pesos(rechargeAmountCents)}`;
        out.push({ key: 'airtime', icon: Smartphone, label: text, phrase: text.toLowerCase() });
    }
    if (levers.extendedWarranty) {
        const text = `Garantía +${String(levers.extendedWarranty.extraMonths)} meses`;
        out.push({ key: 'warranty', icon: CalendarPlus, label: text, phrase: text.toLowerCase() });
    }
    if (levers.walletBonus) {
        out.push({
            key: 'wallet',
            icon: Wallet,
            label: `Bono ${pesos(levers.walletBonus.amountCents)} a tu cuenta Fiado`,
            phrase: `bono ${pesos(levers.walletBonus.amountCents)} a tu cuenta Fiado`,
        });
    }
    return out;
}

/** Orden de las burbujas de venta: regalo, descuento, beneficio y cupón. */
const BUBBLE_RANK: Record<string, number> = {
    gift: 0,
    device: 1,
    accessory: 1,
    airtime: 2,
    warranty: 2,
    wallet: 2,
    coupon: 3,
};

function bubbleRank(bubble: BenefitBubble): number {
    const lever = bubble.key.split('-')[1] ?? '';
    return BUBBLE_RANK[lever] ?? 2;
}

/** `{ tone }` de la palanca, o nada si es verde. */
export function withTone(key: string): Pick<BenefitBubble, 'tone'> {
    const tone = leverTone(key);
    return tone ? { tone } : {};
}

/** Burbujas de las promos propias del equipo (no las de categoría): regalo, descuento, beneficio y cupón. */
export function saleBubblesFor(
    item: CatalogItem,
    promotions: readonly StoreSalePromotionItem[],
): BenefitBubble[] {
    const own = promotions.filter((p) => !isCategoryWide(p) && reachesItem(item, p));
    const bubbles: BenefitBubble[] = [];
    const device = bestDeviceDiscount(item, own);
    if (device) {
        bubbles.push({
            key: 'sale-device',
            label: discountLabel(device.value),
            icon: Tag,
            title: device.promo.name,
        });
    }
    const gifts = own.filter((p) => p.levers.gift);
    if (gifts.length > 1) {
        bubbles.push({
            key: 'sale-gift',
            label: `${String(gifts.length)} regalos`,
            icon: Gift,
            title: gifts.map((p) => p.name).join(' · '),
            tone: 'gift',
        });
    }
    for (const promo of own) {
        for (const lever of otherLevers(promo.levers)) {
            if (lever.key === 'gift' && gifts.length > 1) continue;
            bubbles.push({
                key: `sale-${lever.key}-${promo.promotionId}`,
                label: lever.label,
                icon: lever.icon,
                title: promo.name,
                ...withTone(lever.key),
            });
        }
    }
    return bubbles.sort((a, b) => bubbleRank(a) - bubbleRank(b));
}

/** Precio del equipo con la mejor que va sola más las acumulables, o `null` si ninguna lo baja. */
export function discountedPriceFor(
    item: CatalogItem,
    promotions: readonly StoreSalePromotionItem[],
): number | null {
    const reached = promotions.filter((p) => reachesItem(item, p));
    // La mejor que va sola más la suma de las acumulables, sin pasar del precio.
    const solo = bestDeviceDiscount(
        item,
        reached.filter((p) => !p.stackable),
    );
    const stacked = reached
        .filter((p) => p.stackable && p.levers.deviceDiscount)
        .reduce(
            (sum, p) =>
                sum +
                (p.levers.deviceDiscount
                    ? discountCents(item.cashPriceCents, p.levers.deviceDiscount)
                    : 0),
            0,
        );
    const total = Math.min(item.cashPriceCents, (solo?.cents ?? 0) + stacked);
    return total > 0 ? item.cashPriceCents - total : null;
}

export function cardSaleBenefits(
    item: CatalogItem,
    promotions: readonly StoreSalePromotionItem[],
): CardSaleBenefits {
    const withSureKeep = discountedPriceFor(item, promotions) ?? item.cashPriceCents;
    const cash =
        discountedPriceFor(
            item,
            promotions.filter((p) => !isSureKeepOnly(p)),
        ) ?? item.cashPriceCents;
    return {
        bubbles: saleBubblesFor(item, promotions),
        discountedPriceCents: discountedPriceFor(item, promotions),
        ...(cash !== withSureKeep && { cashPriceCents: cash }),
    };
}

export function joinSpanish(parts: readonly string[], conjunction: 'y' | 'o' | 'ni'): string {
    if (parts.length <= 1) return parts.join('');
    return `${parts.slice(0, -1).join(', ')} ${conjunction} ${String(parts.at(-1))}`;
}

/** «Teléfonos», «Kits y teléfonos» o «Toda la tienda». */
export function categoryScopeLabel(promo: StoreSalePromotionItem): string {
    const categories = promo.scope.productCategories;
    if (categories === null || categories.length === 0) return 'Toda la tienda';
    const names = [...categories]
        .map((c) => CATEGORY_PLURAL[c])
        .sort((a, b) => a.localeCompare(b, 'es-MX'));
    const text = joinSpanish(names, 'y');
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/** «5 de 6 · no kits ni accesorios»: cuántos de la pantalla alcanza y qué deja fuera. */
export function reachDetail(
    promo: StoreSalePromotionItem,
    items: readonly CatalogItem[],
    exclusions: readonly ProductCategory[] = NOTED_EXCLUSIONS,
): string {
    const categories = promo.scope.productCategories;
    const sameKind = items.filter((i) => matchesList(categories, i.category));
    const parts: string[] = [];
    if (sameKind.length > 0) {
        const reached = sameKind.filter((i) => reachesItem(i, promo)).length;
        parts.push(`${String(reached)} de ${String(sameKind.length)}`);
    }
    if (categories !== null && categories.length > 0) {
        const left = exclusions
            .filter((c) => !categories.includes(c))
            .map((c) => CATEGORY_PLURAL[c]);
        if (left.length > 0) parts.push(`no ${joinSpanish(left, 'ni')}`);
    }
    return parts.join(' · ');
}

/** Filas del banner verde: las de categoría o de toda la tienda, en orden alfabético. */
export function saleBannerRows(
    promotions: readonly StoreSalePromotionItem[],
    items: readonly CatalogItem[],
): PromoBannerRow[] {
    return promotions
        .filter(isCategoryWide)
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'))
        .map((promo) => {
            const others = otherLevers(promo.levers);
            const device = promo.levers.deviceDiscount;
            const main = device
                ? { key: 'device', label: discountLabel(device), icon: Tag }
                : others.shift();
            return bannerRowOf(promo, main, others, reachDetail(promo, items));
        });
}

/** Fila del banner: promo, a qué aplica y qué da (la palanca principal y el resto en frase). */
export function bannerRowOf(
    promo: StoreSalePromotionItem,
    main: Pick<LeverText, 'key' | 'label' | 'icon'> | undefined,
    others: readonly LeverText[],
    detail: string,
): PromoBannerRow {
    const note = conditionsText(promo.conditions, promo.blockedBy);
    const badge = paymentOptionsChipLabel(promo);
    return {
        key: promo.promotionId,
        name: promo.name,
        ...(badge !== null && { badge }),
        ...(note !== null && { note }),
        ...(promo.customerText !== '' && { detail: promo.customerText }),
        reach: categoryScopeLabel(promo),
        ...(detail !== '' && { reachDetail: detail }),
        chips: main
            ? [
                  {
                      key: main.key,
                      label: main.label,
                      icon: main.icon,
                      ...withTone(main.key),
                  },
              ]
            : [],
        ...(others.length > 0 && {
            chipsDetail: `+ ${joinSpanish(
                others.map((o) => o.phrase),
                'y',
            )}`,
        }),
    };
}
